import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, LoaderCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BUSINESS, SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "rounded-none border-zinc-800 bg-zinc-950 text-white placeholder:text-zinc-600 focus-visible:border-orange-500 focus-visible:ring-1 focus-visible:ring-orange-500";
const labelCls = "text-xs font-bold uppercase tracking-wider text-zinc-400";

export default function QuoteSection() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please pick a service.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        service: form.service,
        message: form.message,
      });
      toast.success("Quote request sent — we'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try calling us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="border-t border-zinc-800 bg-zinc-900 py-24 lg:py-32" data-testid="quote-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.4 }}
          >
            <p className="font-mono-industrial text-xs font-semibold uppercase tracking-[0.25em] text-orange-500">
              Get in touch
            </p>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
              Get your free written quote
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400">
              Tell us what's going on and we'll come back with a written quote — no
              obligation, no surprises. Rather talk it through? Give us a call.
            </p>

            <div className="mt-10 flex flex-col gap-4">
              <a
                href={`tel:${BUSINESS.phone}`}
                data-testid="contact-call-button"
                className="flex items-center gap-4 border border-zinc-800 bg-zinc-950 p-5 transition-colors duration-150 hover:border-orange-500"
              >
                <span className="flex h-11 w-11 items-center justify-center bg-orange-600 text-white">
                  <Phone className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-wider text-zinc-500">Call or text</span>
                  <span className="font-display text-lg font-bold text-white" data-testid="contact-phone">{BUSINESS.phone}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 border border-zinc-800 bg-zinc-950 p-5" data-testid="contact-email-block">
                <span className="flex h-11 w-11 items-center justify-center border border-zinc-700 text-orange-500">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-wider text-zinc-500">Email</span>
                  <span className="font-display text-lg font-bold text-white" data-testid="contact-email">{BUSINESS.email}</span>
                </span>
              </div>
              <div className="flex items-center gap-4 border border-zinc-800 bg-zinc-950 p-5" data-testid="contact-area-block">
                <span className="flex h-11 w-11 items-center justify-center border border-zinc-700 text-orange-500">
                  <MapPin className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-wider text-zinc-500">Service area</span>
                  <span className="font-display text-lg font-bold text-white" data-testid="contact-area">{BUSINESS.area}</span>
                </span>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="border border-zinc-800 bg-zinc-950 p-8"
            data-testid="quote-form"
          >
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="quote-name" className={labelCls}>Name *</Label>
                <Input id="quote-name" required value={form.name} onChange={set("name")}
                  placeholder="Your name" className={inputCls} data-testid="quote-name-input" />
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="quote-phone" className={labelCls}>Phone *</Label>
                  <Input id="quote-phone" required type="tel" value={form.phone} onChange={set("phone")}
                    placeholder="Best number to reach you" className={inputCls} data-testid="quote-phone-input" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="quote-email" className={labelCls}>Email</Label>
                  <Input id="quote-email" type="email" value={form.email} onChange={set("email")}
                    placeholder="Optional" className={inputCls} data-testid="quote-email-input" />
                </div>
              </div>
              <div className="grid gap-2">
                <Label className={labelCls}>What do you need? *</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger className={inputCls} data-testid="quote-service-select">
                    <SelectValue placeholder="Pick a service" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-zinc-800 bg-zinc-950 text-white">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.name} className="focus:bg-zinc-800 focus:text-white" data-testid={`quote-service-option-${s.id}`}>
                        {s.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="Other / Not sure" className="focus:bg-zinc-800 focus:text-white" data-testid="quote-service-option-other">
                      Other / Not sure
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="quote-message" className={labelCls}>Tell us about the job *</Label>
                <Textarea id="quote-message" required rows={4} value={form.message} onChange={set("message")}
                  placeholder="e.g. Windmill not pumping, needs a look — property near..."
                  className={inputCls} data-testid="quote-message-input" />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="quote-submit-button"
                className="inline-flex items-center justify-center gap-2 border border-orange-600 bg-orange-600 px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors duration-150 hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? <LoaderCircle className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                {sending ? "Sending..." : "Request Free Quote"}
              </button>
              <p className="text-xs leading-relaxed text-zinc-500">
                No spam, no obligation. We'll only use your details to get your quote sorted.
              </p>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
